import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWeather } from '@/hooks/useWeather';
import { useEvents } from '@/hooks/useEvents';
import { useSearch } from '@/hooks/useSearch';
import { HeroBanner } from '@/components/HeroBanner';
import { WeatherInfographic } from '@/components/WeatherCard';
import { TodayCard } from '@/components/TodayCard';
import { SearchIcon, ChevronRightIcon } from '@/components/Icons';
import { PlaceCard } from '@/components/PlaceCard';
import { useFavorites } from '@/hooks/useFavorites';
import { formatEventDayLabel, formatEventTimeRange, shortEventLocation } from '@shared/events';
import { takeMeHomeDirectionsUrl } from '@shared/take-me-home';
import { appendEnglish } from '@shared/ptEn';
import { Bilingual } from '@/components/Bilingual';
import { FixTranslation } from '@/components/FixTranslation';
import { fallbackWeather } from '@shared/openMeteo';

function SearchResults({ query }: { query: string }) {
  const { results, loading } = useSearch(query);
  const { isFavorite, toggle } = useFavorites();
  if (!query.trim()) return null;
  return (
    <div style={{ marginTop: 12 }}>
      {loading && <p className="muted">Searching…</p>}
      {!loading && results.length === 0 && <p className="muted">No matches for “{query}”.</p>}
      {results.map((r) =>
        r.place ? (
          <PlaceCard key={r.place.id} place={r.place} isFavorite={isFavorite(r.place.id)} onToggleFavorite={toggle} />
        ) : (
          <Link key={`${r.type}-${r.href}-${r.label}`} to={r.href} className="list-row">
            <span>{r.label}</span>
            <ChevronRightIcon />
          </Link>
        ),
      )}
    </div>
  );
}

export function HomePage() {
  const [query, setQuery] = useState('');
  const { weather } = useWeather();
  const { today, events, nextFive, loading: loadingEvents, applyTitleEnglish } = useEvents();
  const weatherCard = weather ?? fallbackWeather();

  return (
    <div className="home-page">
      <HeroBanner />

      <div className="pill-row">
        <a className="home-pill" href={takeMeHomeDirectionsUrl()} target="_blank" rel="noreferrer">
          Take me home
        </a>
      </div>

      <TodayCard today={today} weather={weatherCard} events={events} loading={loadingEvents} />

      <WeatherInfographic weather={weatherCard} />

      <div className="hero-whats-on">
        <Link to="/events" className="hero-whats-on__head">
          <span>What’s On · next 5 days</span>
          <ChevronRightIcon />
        </Link>
        {!loadingEvents && events.length === 0 && (
          <p className="hero-whats-on__empty">No events loaded yet</p>
        )}
        {!loadingEvents && events.length > 0 && nextFive.length === 0 && (
          <p className="hero-whats-on__empty">Nothing listed in the next 5 days.</p>
        )}
        {nextFive.map((e) => {
          const where = shortEventLocation(e.location);
          const meta = [
            formatEventDayLabel(e.date, today ?? e.date),
            formatEventTimeRange(e),
            where ? appendEnglish(where, where) : '',
            e.source ? appendEnglish(e.source, e.source) : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={e.id} className="hero-whats-on__row">
              <Link to={`/events#event-${e.id}`} className="hero-whats-on__link">
                <strong>
                  <Bilingual text={e.title} english={e.titleEn} />
                </strong>
                <span className="hero-whats-on__meta">{meta}</span>
              </Link>
              <FixTranslation title={e.title} onFixed={(english) => applyTitleEnglish(e.title, english)} />
            </div>
          );
        })}
      </div>

      <form
        className="home-search"
        onSubmit={(e) => {
          e.preventDefault();
        }}
        role="search"
      >
        <div className="search-box">
          <SearchIcon />
          <label htmlFor="home-search" className="visually-hidden">
            What do you need?
          </label>
          <input
            id="home-search"
            type="search"
            placeholder="What do you need?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            enterKeyHint="search"
          />
        </div>
      </form>
      <SearchResults query={query} />
    </div>
  );
}
